import { Loader2, Save, Trash2, X } from 'lucide-react';
import React from 'react';

import { titleCase } from '../../lib/format-utils';
import { FormatSelect } from './FormatSelect';
import { ARRAY_ITEM_TYPES, EXPOSURE_SURFACES, JSON_SCHEMA_TYPES, TYPE_LABELS } from './helpers';
import type {
  AddMappingOption,
  ArrayItemType,
  AttributeExposureState,
  ExposureSurfaceKey,
  ExposureToggleProps,
  JsonSchemaType,
  MappingChip,
  SelectOption,
} from './schema-editor.types';
import styles from './schema-editor.module.css';

interface AttributeEditorPanelProps {
  kicker: string;
  fieldName: string;
  onNameChange: (value: string) => void;

  type: JsonSchemaType;
  onTypeChange: (value: JsonSchemaType) => void;

  // The type of a single element when `type` is `array`. Arrays cannot nest.
  itemsType: ArrayItemType | '';
  onItemsTypeChange: (value: ArrayItemType) => void;

  // For an array, this is the format of its items — the array itself has none.
  format: string;
  onFormatChange: (value: string) => void;

  // True for a reference attribute and for an array of references.
  showReference: boolean;
  referenceValue: string;
  schemaOptions: SelectOption[];
  onReferenceChange: (value: string) => void;

  enumValue: string;
  onEnumChange: (value: string) => void;

  required: boolean;
  onRequiredChange: (value: boolean) => void;

  mappingChips: MappingChip[];
  addMappingOptions: AddMappingOption[];
  onAddMapping: (type: string) => void;
  onRemoveMapping: (type: string) => void;

  showStatusValues: boolean;
  statusActiveValue: string;
  statusInactiveValue: string;
  onStatusChange: (which: 'activeValue' | 'deactivatedValue', value: string) => void;

  showAmountValues: boolean;
  amountCurrencyField: string;
  amountCurrency: string;
  amountFieldOptions: string[];
  currencyOptions: string[];
  onAmountChange: (which: 'currencyField' | 'currency', value: string) => void;

  description: string;
  onDescriptionChange: (value: string) => void;

  exposure: AttributeExposureState;
  onExposureToggle: (surface: ExposureSurfaceKey, value: boolean) => void;
  exposureName: string;
  onExposureNameChange: (value: string) => void;
  exposureNameRequired: boolean;
  mergeToken: string;
  // Agentic Tools sits behind `enable-partner-mcp-authoring`; an attribute that
  // is already exposed to it keeps the toggle so the exposure stays clearable.
  showAgentTools: boolean;
  // Schema-wide: true when the flag is on OR any attribute already exposes to
  // agent tools, matching where the rest of the Agentic Tools guidance shows.
  showAgentToolsHint: boolean;

  onRemove: () => void;
  onClose: () => void;

  // Saving is schema-wide, but surfacing it here — in the header and above the
  // fold at the bottom of the panel — nudges people to commit each attribute
  // edit as they go instead of banking a large unsaved draft.
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
}

const AGENT_TOOLS_EXPOSURE_NAME_HINT =
  ' For Agentic Tools, this is the key used for the field in returned results.';

const ExposureToggle: React.FC<ExposureToggleProps> = ({ title, help, checked, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={title}
    className={styles.editorExposureCard}
    data-on={checked}
    onClick={onToggle}>
    <span className={styles.editorExposureText}>
      <strong>{title}</strong>
      <span>{help}</span>
    </span>
    <span aria-hidden="true" data-on={checked} className={styles.editorToggle} />
  </button>
);

export const AttributeEditorPanel: React.FC<AttributeEditorPanelProps> = ({
  kicker,
  fieldName,
  onNameChange,
  type,
  onTypeChange,
  itemsType,
  onItemsTypeChange,
  format,
  onFormatChange,
  showReference,
  referenceValue,
  schemaOptions,
  onReferenceChange,
  enumValue,
  onEnumChange,
  required,
  onRequiredChange,
  mappingChips,
  addMappingOptions,
  onAddMapping,
  onRemoveMapping,
  showStatusValues,
  statusActiveValue,
  statusInactiveValue,
  onStatusChange,
  showAmountValues,
  amountCurrencyField,
  amountCurrency,
  amountFieldOptions,
  currencyOptions,
  onAmountChange,
  description,
  onDescriptionChange,
  exposure,
  onExposureToggle,
  exposureName,
  onExposureNameChange,
  exposureNameRequired,
  mergeToken,
  showAgentTools,
  showAgentToolsHint,
  onRemove,
  onClose,
  dirty,
  saving,
  onSave,
}) => {
  const panelLabel = `Edit attribute ${fieldName}`;
  const isArray = type === 'array';
  const exposureSurfaces = EXPOSURE_SURFACES.filter(
    surface => surface.key !== 'agentTools' || showAgentTools || exposure.agentTools,
  );
  const hasMappings = mappingChips.length > 0;
  const addMappingChoices = addMappingOptions.map(option => ({
    value: option.value,
    label: option.required ? `${option.label} (Required)` : option.label,
  }));

  return (
    <aside className={styles.editorPanel} aria-label={panelLabel}>
      <header className={styles.editorHeader}>
        <div className={styles.editorTitleGroup}>
          <span className={styles.editorKicker}>{kicker}</span>
          <p className={styles.editorFieldName}>{fieldName}</p>
        </div>
        <div className={styles.editorHeaderActions}>
          {dirty && (
            <button
              type="button"
              className={styles.editorHeaderSaveButton}
              onClick={onSave}
              disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
          )}
          <button
            type="button"
            className={styles.editorCloseButton}
            aria-label="Close editor"
            onClick={onClose}>
            <X size={18} />
          </button>
        </div>
      </header>

      <div className={styles.editorBody}>
        <section className={styles.editorBasics}>
          <label className={styles.editorField}>
            Field name
            <input type="text" value={fieldName} onChange={event => onNameChange(event.target.value)} />
          </label>

          <div className={styles.editorFieldRow}>
            <label className={styles.editorField}>
              Type
              <select
                value={type}
                onChange={event => onTypeChange(event.target.value as JsonSchemaType)}>
                {JSON_SCHEMA_TYPES.map(schemaType => (
                  <option key={schemaType} value={schemaType}>
                    {TYPE_LABELS[schemaType]}
                  </option>
                ))}
              </select>
            </label>

            {isArray ? (
              <label className={styles.editorField}>
                Array of
                <select
                  value={itemsType}
                  onChange={event => onItemsTypeChange(event.target.value as ArrayItemType)}>
                  {ARRAY_ITEM_TYPES.map(schemaType => (
                    <option key={schemaType} value={schemaType}>
                      {TYPE_LABELS[schemaType]}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <FormatSelect label="Format" value={format} onChange={onFormatChange} />
            )}
          </div>

          {isArray && itemsType === 'string' && (
            <div className={styles.editorFieldRow}>
              <FormatSelect label="Item format" value={format} onChange={onFormatChange} />
            </div>
          )}

          {showReference && (
            <label className={styles.editorField}>
              References schema
              <select value={referenceValue} onChange={event => onReferenceChange(event.target.value)}>
                <option value="">Select a schema…</option>
                {schemaOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {isArray && <small>Every item in the array points at a record in this schema.</small>}
            </label>
          )}

          <label className={styles.editorField}>
            Enum values
            <input
              type="text"
              value={enumValue}
              placeholder="Leave empty for any value"
              onChange={event => onEnumChange(event.target.value)}
            />
            <small>Comma-separated. Only these values will be accepted.</small>
          </label>

          <label className={styles.editorCheckboxRow}>
            <input type="checkbox" checked={required} onChange={() => onRequiredChange(!required)} />
            Required on every record
          </label>
        </section>

        <hr className={styles.editorDivider} />

        <section className={styles.editorSection}>
          <div className={styles.editorIntro}>
            <h2>Mapping</h2>
            <p>
              Tell Reach which core concept this attribute represents so it can power segments,
              messaging, and automations.
            </p>
          </div>

          {hasMappings ? (
            <ul className={styles.editorChips}>
              {mappingChips.map(chip => (
                <li key={chip.type}>
                  {chip.label}
                  {chip.required && <abbr title="Required">*</abbr>}
                  <button
                    type="button"
                    aria-label={`Remove ${chip.label} mapping`}
                    onClick={() => onRemoveMapping(chip.type)}>
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.editorEmptyMapping}>Not mapped to a core concept</p>
          )}

          <select
            className={styles.editorAddMapping}
            value=""
            aria-label="Add mapping"
            onChange={event => {
              if (event.target.value) onAddMapping(event.target.value);
            }}>
            <option value="">+ Add mapping…</option>
            {addMappingChoices.map(choice => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>

          {showStatusValues && (
            <div className={styles.editorSubBox}>
              <h3>Mapping values</h3>
              <label className={styles.editorField}>
                Active value
                <input
                  type="text"
                  value={statusActiveValue}
                  onChange={event => onStatusChange('activeValue', event.target.value)}
                />
              </label>
              <label className={styles.editorField}>
                Inactive value
                <input
                  type="text"
                  value={statusInactiveValue}
                  onChange={event => onStatusChange('deactivatedValue', event.target.value)}
                />
              </label>
            </div>
          )}

          {showAmountValues && (
            <div className={styles.editorSubBox}>
              <h3>Mapping values</h3>
              <label className={styles.editorField}>
                Currency field
                <select
                  value={amountCurrencyField}
                  onChange={event => onAmountChange('currencyField', event.target.value)}>
                  <option value="">Select a field…</option>
                  {amountFieldOptions.map(fieldOption => (
                    <option key={fieldOption} value={fieldOption}>
                      {fieldOption}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.editorField}>
                Currency code
                <select
                  value={amountCurrency}
                  onChange={event => onAmountChange('currency', event.target.value)}>
                  <option value="">Select a currency…</option>
                  {currencyOptions.map(currencyOption => (
                    <option key={currencyOption} value={currencyOption}>
                      {currencyOption}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </section>

        <hr className={styles.editorDivider} />

        <section className={styles.editorSection}>
          <div className={styles.editorIntro}>
            <h2>Exposure</h2>
            <p>Where this attribute can be used by you, your tenants, and agents.</p>
          </div>

          <fieldset className={styles.editorToggles}>
            <legend>Exposure surfaces</legend>
            {exposureSurfaces.map(surface => (
              <ExposureToggle
                key={surface.key}
                title={surface.title}
                help={surface.help}
                checked={exposure[surface.key]}
                onToggle={() => onExposureToggle(surface.key, !exposure[surface.key])}
              />
            ))}
          </fieldset>

          <div className={styles.editorNamingBlock}>
            <label className={styles.editorField}>
              Display name
              <input
                type="text"
                value={exposureName}
                placeholder={`e.g. ${titleCase(fieldName) || 'Customer Email'}`}
                onChange={event => onExposureNameChange(event.target.value)}
              />
              <small>
                The label people and agents see instead of the raw field name.
                {showAgentToolsHint && AGENT_TOOLS_EXPOSURE_NAME_HINT}
              </small>
            </label>
            {exposureNameRequired && (
              <p className={styles.editorExposureError}>
                A display name is required before this attribute can be used anywhere.
              </p>
            )}
            <label className={styles.editorField}>
              Description
              <textarea
                rows={3}
                value={description}
                placeholder="What this attribute holds and how someone building a segment, a message, or an agent should use it."
                onChange={event => onDescriptionChange(event.target.value)}
              />
            </label>
            {exposure.agentTools && (
              <p className={styles.editorAgentWarning}>
                Agents can read this value out loud on a call. Leave it off for gate codes, balances, or
                anything you would not say to an unverified caller.
              </p>
            )}
            {exposure.mergeFields && mergeToken && (
              <p className={styles.editorMergeToken}>{mergeToken}</p>
            )}
          </div>
        </section>

        <hr className={styles.editorDivider} />

        <button type="button" className={styles.editorRemoveButton} onClick={onRemove}>
          <Trash2 size={16} />
          Remove attribute
        </button>
      </div>

      {dirty && (
        <footer className={styles.editorSaveBar}>
          <p className={styles.editorSaveHint}>Unsaved changes</p>
          <button type="button" className={styles.editorSaveButton} onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      )}
    </aside>
  );
};
